import {FC, InputHTMLAttributes, ReactNode} from 'react';
import {FieldError, RegisterOptions, useFormContext} from 'react-hook-form';
import clsx from 'clsx';
import CheckboxRadioGroup from 'components/Form/CheckboxRadioGroup';
import {RequiredRules} from 'components/Form/types';
import {getRegisterRules} from 'components/Form/utils';
import TooltipWrapper from 'components/Tooltip';
import {useTooltip} from 'components/Tooltip/hook';
import useFormError from 'hooks/useFormErrors';

export type RadioOption = {
    disabled?: boolean;
    disabledReason?: string;
    label: ReactNode;
    value: string;
};

type RadioOptionProps = RadioOption &
    Pick<BaseRadioProps, 'error' | 'id' | 'readOnly' | 'name' | 'required'> & {
        inputProps: InputHTMLAttributes<HTMLInputElement>;
        registerRules?: RegisterOptions;
    };

const BaseRadioOption: FC<RadioOptionProps> = ({
    disabled,
    error,
    id,
    inputProps,
    label,
    name,
    readOnly,
    registerRules,
    required,
    value,
}) => {
    const {register} = useFormContext();

    const {setTooltipRef} = useTooltip();

    return (
        <label
            key={value}
            ref={(r) => setTooltipRef(r, value)}
            className={clsx(
                'flex w-fit select-none items-center',
                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            htmlFor={`${id || name}-${value}`}
        >
            <input
                className={clsx(
                    error &&
                        'border-coral-400 ring ring-coral-100 ring-offset-0',
                )}
                disabled={disabled || readOnly}
                id={`${id || name}-${value}`}
                readOnly={readOnly}
                required={!!(required && error)}
                tabIndex={readOnly ? -1 : undefined}
                type="radio"
                {...register(name, registerRules)}
                aria-label={`${id || name}-${value}`}
                value={value}
                {...inputProps}
            />
            <div
                className={clsx(
                    'ml-2',
                    disabled || readOnly
                        ? 'text-light-disabled'
                        : 'text-grey-900',
                )}
            >
                {label}
            </div>
        </label>
    );
};

BaseRadioOption.displayName = 'BaseRadioOption';

export type BaseRadioProps<T = HTMLInputElement> = RequiredRules<
    InputHTMLAttributes<T>
> & {
    error?: FieldError;
    isHorizontal?: boolean;
    name: string;
    options: RadioOption[];
    type?: never;
};

const BaseRadio: FC<BaseRadioProps> = ({
    children,
    className,
    disabled,
    error,
    id,
    isHorizontal,
    name,
    options,
    readOnly,
    required,
    rules,
    ...rest
}) => {
    const registerRules = getRegisterRules({disabled, required, rules});

    const fieldError = useFormError(name, error);

    return (
        <CheckboxRadioGroup
            className={clsx('text-base', className)}
            isHorizontal={isHorizontal}
        >
            {options.map((option) => (
                <TooltipWrapper
                    key={option.value}
                    message={option.disabled && option.disabledReason}
                >
                    <BaseRadioOption
                        disabled={
                            option.disabled ||
                            readOnly ||
                            registerRules?.disabled
                        }
                        error={fieldError}
                        id={id}
                        inputProps={rest}
                        label={option.label}
                        name={name}
                        readOnly={readOnly}
                        registerRules={registerRules}
                        value={option.value}
                    />
                </TooltipWrapper>
            ))}
            {children}
        </CheckboxRadioGroup>
    );
};

export default BaseRadio;
